/**
 * IpApiPage — admin lookup tool for the ip-api.com pro endpoint.
 * Features:
 *   • Single-IP lookup with a rich result panel (country, region, city,
 *     ISP, org, ASN, timezone, mobile/proxy/hosting flags)
 *   • Leaflet map pin at the returned lat/lon
 *   • Bulk lookup — paste up to 100 IPs at once
 *   • Persistent history table (last 100 lookups, saved in Mongo) with
 *     CSV export + row delete + "Show on map" quick action
 *   • "Look up my own IP" button
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Search, Globe2, MapPin, Server, Wifi, Loader2, Copy, Trash2, Download,
  Layers, Locate, ShieldAlert, Radio, Building2, Clock, ExternalLink,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix Leaflet's marker icon URLs — CRA doesn't proxy the images by default.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const errText = (e, f) => e?.response?.data?.detail || e?.message || f;
const fmt = (v) => (v == null || v === "" ? "—" : String(v));

// Helper to imperatively fly to a location when `focus` prop changes
function MapFocus({ focus }) {
  const map = useMap();
  useEffect(() => {
    if (focus && Number.isFinite(focus.lat) && Number.isFinite(focus.lon)) {
      map.flyTo([focus.lat, focus.lon], focus.zoom || 10,
        { duration: 0.7 });
    }
  }, [focus, map]);
  return null;
}

// A single detail row inside the result panel
function Row({ icon: Icon, label, value, mono, copy }) {
  const doCopy = () => {
    if (!value) return;
    navigator.clipboard.writeText(String(value));
    toast.success("Copied");
  };
  return (
    <div className="flex items-start gap-2 py-1">
      <Icon className="h-3.5 w-3.5 text-zinc-400 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
        <div className={`text-sm ${mono ? "font-mono" : ""} break-words`}>
          {fmt(value)}
        </div>
      </div>
      {copy && value && (
        <button onClick={doCopy}
          className="text-zinc-400 hover:text-[#002FA7] mt-0.5"
          title="Copy">
          <Copy className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function FlagBadges({ result }) {
  const badges = [];
  if (result.mobile) badges.push({ label: "Mobile carrier", color: "bg-sky-100 text-sky-800 border-sky-200", icon: Radio });
  if (result.proxy) badges.push({ label: "Proxy / VPN", color: "bg-red-100 text-red-800 border-red-200", icon: ShieldAlert });
  if (result.hosting) badges.push({ label: "Hosting / datacenter", color: "bg-amber-100 text-amber-800 border-amber-200", icon: Server });
  if (!badges.length) return null;
  return (
    <div className="flex gap-1.5 flex-wrap">
      {badges.map((b) => (
        <Badge key={b.label}
          className={`rounded-sm text-[10px] px-1.5 py-0.5 border ${b.color}`}>
          <b.icon className="h-3 w-3 mr-1" /> {b.label}
        </Badge>
      ))}
    </div>
  );
}

export default function IpApiPage() {
  const [ip, setIp] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [focus, setFocus] = useState(null);
  const [mode, setMode] = useState("single"); // 'single' | 'bulk'
  const [bulkText, setBulkText] = useState("");
  const [bulkResults, setBulkResults] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get("/ip-api/history?limit=200");
      setHistory(data.lookups || []);
    } catch (e) {
      toast.error(errText(e, "Failed to load history"));
    } finally { setHistoryLoading(false); }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/ip-api/config");
        setConfig(data);
      } catch { /* no-op */ }
    })();
    loadHistory();
  }, [loadHistory]);

  // Support ?ip=... deep-links from Security Center
  const initedRef = useRef(false);
  useEffect(() => {
    if (initedRef.current) return;
    const p = new URLSearchParams(window.location.search);
    const q = p.get("ip");
    if (q) {
      initedRef.current = true;
      setIp(q);
      // trigger lookup after mount
      setTimeout(() => doLookup(q), 100);
    } else {
      initedRef.current = true;
    }
  }, []);

  const doLookup = async (override) => {
    const targetIp = (override || ip || "").trim();
    if (!targetIp) { toast.error("Enter an IP address"); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/ip-api/lookup", {
        ip: targetIp, note: note.trim() || null, save: true,
      });
      setResult(data);
      if (Number.isFinite(data.lat) && Number.isFinite(data.lon)) {
        setFocus({ lat: data.lat, lon: data.lon, zoom: 10 });
      }
      loadHistory();
    } catch (e) {
      toast.error(errText(e, "Lookup failed"));
    } finally { setBusy(false); }
  };

  const doSelfLookup = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.get("/ip-api/self");
      setResult(data);
      setIp(data.ip || "");
      if (Number.isFinite(data.lat) && Number.isFinite(data.lon)) {
        setFocus({ lat: data.lat, lon: data.lon, zoom: 10 });
      }
      loadHistory();
    } catch (e) {
      toast.error(errText(e, "Lookup failed"));
    } finally { setBusy(false); }
  };

  const doBulk = async () => {
    const list = bulkText.split(/[\s,;\n\r]+/).map((s) => s.trim()).filter(Boolean);
    if (!list.length) { toast.error("Paste some IPs first"); return; }
    if (list.length > 100) {
      toast.error("Max 100 IPs at a time"); return;
    }
    setBulkBusy(true); setBulkResults([]);
    try {
      const { data } = await api.post("/ip-api/batch", {
        ips: list, note: note.trim() || null, save: true,
      });
      setBulkResults(data.results || []);
      toast.success(`Looked up ${data.count} IPs`);
      loadHistory();
    } catch (e) {
      toast.error(errText(e, "Batch failed"));
    } finally { setBulkBusy(false); }
  };

  const deleteRow = async (lookup_id) => {
    if (!window.confirm("Remove this lookup from history?")) return;
    try {
      await api.delete(`/ip-api/history/${lookup_id}`);
      setHistory((rows) => rows.filter((r) => r.lookup_id !== lookup_id));
    } catch (e) { toast.error(errText(e, "Delete failed")); }
  };

  const clearHistory = async () => {
    if (!window.confirm(`Clear all ${history.length} history entries? This cannot be undone.`)) return;
    try {
      await api.post("/ip-api/history/clear");
      setHistory([]);
      toast.success("History cleared");
    } catch (e) { toast.error(errText(e, "Clear failed")); }
  };

  const showOnMap = (row) => {
    if (Number.isFinite(row.lat) && Number.isFinite(row.lon)) {
      setFocus({ lat: row.lat, lon: row.lon, zoom: 11 });
      setResult(row);
      setIp(row.ip || "");
    } else {
      toast.error("No coordinates available");
    }
  };

  const exportCsv = () => {
    const cols = ["ip", "country", "region_name", "city", "zip", "lat", "lon",
      "isp", "org", "as_number", "as_name", "timezone",
      "mobile", "proxy", "hosting", "note", "created_at"];
    const escape = (v) => {
      if (v == null) return "";
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [cols.join(",")].concat(
      history.map((r) => cols.map((c) => escape(r[c])).join(","))
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `ip-lookups-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const pins = useMemo(
    () => history.filter((r) => Number.isFinite(r.lat) && Number.isFinite(r.lon)),
    [history]);

  return (
    <div className="p-6 max-w-[1500px] mx-auto space-y-4" data-testid="ip-api-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-500">
            <Globe2 className="h-3.5 w-3.5" />
            <span>System · Network intelligence</span>
          </div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mt-0.5">
            IP API
          </h1>
          <p className="text-sm text-zinc-600 mt-1 max-w-2xl">
            Look up geolocation, ISP, ASN, mobile-carrier and proxy signals for
            any IPv4/IPv6 via ip-api.com. Every lookup is saved for reference.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {config && (
            <Badge className={`rounded-sm text-[10px] ${config.configured
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : "bg-amber-50 text-amber-800 border-amber-200"}`}
              data-testid="ip-api-config-badge">
              {config.configured
                ? `Pro key active (${config.endpoint})`
                : "Free tier — set IP_API_KEY for pro"}
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={doSelfLookup}
            data-testid="ip-api-self-btn">
            <Locate className="h-4 w-4 mr-1.5" /> My IP
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left — lookup form + result */}
        <div className="space-y-4">
          <Card className="rounded-sm">
            <CardContent className="p-4 space-y-3">
              <div className="flex gap-1 -mx-1">
                <button type="button"
                  onClick={() => setMode("single")}
                  className={`flex-1 rounded-sm border px-3 py-1.5 text-xs
                    ${mode === "single" ? "border-[#002FA7] bg-[#002FA7]/5 text-[#002FA7] font-semibold"
                      : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}
                  data-testid="ip-api-tab-single">
                  Single IP
                </button>
                <button type="button"
                  onClick={() => setMode("bulk")}
                  className={`flex-1 rounded-sm border px-3 py-1.5 text-xs
                    ${mode === "bulk" ? "border-[#002FA7] bg-[#002FA7]/5 text-[#002FA7] font-semibold"
                      : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}
                  data-testid="ip-api-tab-bulk">
                  Bulk (up to 100)
                </button>
              </div>

              {mode === "single" && (
                <>
                  <div>
                    <Label className="text-[11px]">IP address</Label>
                    <div className="flex gap-2">
                      <Input value={ip} onChange={(e) => setIp(e.target.value)}
                        placeholder="8.8.8.8 or 2606:4700::"
                        onKeyDown={(e) => e.key === "Enter" && doLookup()}
                        className="font-mono"
                        data-testid="ip-api-input" />
                      <Button onClick={() => doLookup()} disabled={busy || !ip.trim()}
                        className="bg-[#002FA7] hover:bg-[#001F7A]"
                        data-testid="ip-api-lookup-btn">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                          : <><Search className="h-4 w-4 mr-1.5" /> Look up</>}
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-[11px]">Note (optional)</Label>
                    <Input value={note} onChange={(e) => setNote(e.target.value)}
                      placeholder="e.g. Suspicious login attempt on darren@..."
                      data-testid="ip-api-note" />
                  </div>
                </>
              )}

              {mode === "bulk" && (
                <>
                  <div>
                    <Label className="text-[11px]">
                      IPs (one per line, or comma/space separated — max 100)
                    </Label>
                    <textarea rows={6}
                      value={bulkText}
                      onChange={(e) => setBulkText(e.target.value)}
                      className="w-full rounded-sm border border-zinc-300 px-2 py-1.5 text-xs font-mono"
                      placeholder="8.8.8.8&#10;1.1.1.1&#10;208.67.222.222"
                      data-testid="ip-api-bulk-input" />
                    <div className="text-[10px] text-zinc-500 mt-1">
                      {bulkText.split(/[\s,;\n\r]+/).filter(Boolean).length} IPs parsed
                    </div>
                  </div>
                  <div>
                    <Label className="text-[11px]">Note (optional)</Label>
                    <Input value={note} onChange={(e) => setNote(e.target.value)}
                      data-testid="ip-api-bulk-note" />
                  </div>
                  <Button onClick={doBulk} disabled={bulkBusy || !bulkText.trim()}
                    className="w-full bg-[#002FA7] hover:bg-[#001F7A]"
                    data-testid="ip-api-bulk-btn">
                    {bulkBusy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Looking up…</>
                      : <><Layers className="h-4 w-4 mr-1.5" /> Look up all</>}
                  </Button>
                  {bulkResults.length > 0 && (
                    <div className="text-[11px] text-zinc-600">
                      Batch complete — {bulkResults.length} results now in the
                      history table below.
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Result panel */}
          {result && (
            <Card className="rounded-sm" data-testid="ip-api-result">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                      Result
                    </div>
                    <div className="font-mono text-lg font-bold">{result.ip}</div>
                  </div>
                  <FlagBadges result={result} />
                </div>
                <div className="pt-2 border-t border-zinc-100 grid grid-cols-1 gap-0.5">
                  <Row icon={MapPin} label="Location"
                    value={[result.city, result.region_name, result.country]
                      .filter(Boolean).join(", ")} />
                  <Row icon={MapPin} label="Postcode" value={result.zip} mono />
                  <Row icon={MapPin} label="Coordinates"
                    value={result.lat != null && result.lon != null
                      ? `${result.lat}, ${result.lon}` : null} mono copy />
                  <Row icon={Clock} label="Timezone" value={result.timezone} />
                  <Row icon={Wifi} label="ISP" value={result.isp} copy />
                  <Row icon={Building2} label="Organisation" value={result.org} copy />
                  <Row icon={Server} label="AS number" value={result.as_number} mono copy />
                  <Row icon={Server} label="AS name" value={result.as_name} />
                  <Row icon={Globe2} label="Reverse DNS" value={result.reverse} mono />
                </div>
                <div className="flex gap-2 flex-wrap pt-2 border-t border-zinc-100">
                  {Number.isFinite(result.lat) && Number.isFinite(result.lon) && (
                    <a href={`https://www.google.com/maps?q=${result.lat},${result.lon}`}
                      target="_blank" rel="noreferrer"
                      className="text-xs text-[#002FA7] hover:underline inline-flex items-center gap-1">
                      <ExternalLink className="h-3 w-3" /> Google Maps
                    </a>
                  )}
                  <a href={`https://ip-api.com/${result.ip}`}
                    target="_blank" rel="noreferrer"
                    className="text-xs text-[#002FA7] hover:underline inline-flex items-center gap-1">
                    <ExternalLink className="h-3 w-3" /> Open on ip-api.com
                  </a>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right — Leaflet map */}
        <div className="lg:col-span-2">
          <Card className="rounded-sm overflow-hidden">
            <CardContent className="p-0" style={{ height: 520 }}
              data-testid="ip-api-map">
              <MapContainer
                center={[-25.2744, 133.7751]}
                zoom={4}
                style={{ height: "100%", width: "100%" }}
                scrollWheelZoom>
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                {pins.map((r) => (
                  <Marker key={r.lookup_id} position={[r.lat, r.lon]}>
                    <Popup>
                      <div className="text-xs">
                        <div className="font-mono font-bold">{r.ip}</div>
                        <div>{[r.city, r.region_name, r.country].filter(Boolean).join(", ")}</div>
                        <div className="text-zinc-500">{r.isp}</div>
                        {r.hosting && <div className="text-amber-700">Hosting / datacenter</div>}
                        {r.proxy && <div className="text-red-700">Proxy / VPN</div>}
                      </div>
                    </Popup>
                  </Marker>
                ))}
                <MapFocus focus={focus} />
              </MapContainer>
            </CardContent>
          </Card>
          <div className="text-[10px] text-zinc-500 mt-1 flex items-center gap-2">
            <MapPin className="h-3 w-3" />
            {pins.length} pin{pins.length === 1 ? "" : "s"} · click any history row →
            &quot;Show on map&quot; to jump to it. Australia Post postcode overlay
            coming next (upload the CSV and I&apos;ll wire it in).
          </div>
        </div>
      </div>

      {/* History */}
      <Card className="rounded-sm">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span className="font-display font-bold text-sm">Lookup history</span>
              <Badge className="rounded-sm bg-zinc-100 text-zinc-700 border-zinc-200 text-[10px]">
                {history.length}
              </Badge>
              {historyLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-400" />}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={loadHistory}
                data-testid="ip-api-history-refresh">
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
              </Button>
              <Button size="sm" variant="outline" onClick={exportCsv}
                disabled={!history.length}
                data-testid="ip-api-history-csv">
                <Download className="h-3.5 w-3.5 mr-1.5" /> CSV
              </Button>
              <Button size="sm" variant="outline" onClick={clearHistory}
                disabled={!history.length}
                className="text-red-700 border-red-200 hover:bg-red-50"
                data-testid="ip-api-history-clear">
                <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Clear
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
                  <th className="py-1.5 pr-3">When</th>
                  <th className="py-1.5 pr-3">IP</th>
                  <th className="py-1.5 pr-3">Location</th>
                  <th className="py-1.5 pr-3">ISP</th>
                  <th className="py-1.5 pr-3">Flags</th>
                  <th className="py-1.5 pr-3">Note</th>
                  <th className="py-1.5" />
                </tr>
              </thead>
              <tbody>
                {history.length === 0 && !historyLoading && (
                  <tr><td colSpan={7} className="py-8 text-center text-zinc-500 italic">
                    No lookups yet — search above to get started.
                  </td></tr>
                )}
                {history.map((r) => (
                  <tr key={r.lookup_id} className="border-b border-zinc-100 hover:bg-zinc-50"
                    data-testid={`ip-api-history-row-${r.lookup_id}`}>
                    <td className="py-2 pr-3 text-zinc-500 whitespace-nowrap">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className="py-2 pr-3 font-mono font-semibold">{r.ip}</td>
                    <td className="py-2 pr-3">
                      {[r.city, r.region_name, r.country].filter(Boolean).join(", ") || "—"}
                    </td>
                    <td className="py-2 pr-3 text-zinc-600 truncate max-w-[220px]" title={r.isp}>
                      {r.isp || "—"}
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex gap-1">
                        {r.hosting && <Badge className="rounded-sm text-[9px] bg-amber-100 text-amber-800 border-amber-200">HOST</Badge>}
                        {r.proxy && <Badge className="rounded-sm text-[9px] bg-red-100 text-red-800 border-red-200">PROXY</Badge>}
                        {r.mobile && <Badge className="rounded-sm text-[9px] bg-sky-100 text-sky-800 border-sky-200">MOBILE</Badge>}
                      </div>
                    </td>
                    <td className="py-2 pr-3 text-zinc-500 italic max-w-[200px] truncate" title={r.note}>
                      {r.note || "—"}
                    </td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px]"
                        onClick={() => showOnMap(r)}
                        data-testid={`ip-api-history-show-${r.lookup_id}`}>
                        <MapPin className="h-3 w-3 mr-1" /> Show
                      </Button>
                      <Button size="sm" variant="ghost"
                        className="h-6 px-1.5 text-[10px] text-red-600 hover:text-red-700"
                        onClick={() => deleteRow(r.lookup_id)}
                        data-testid={`ip-api-history-delete-${r.lookup_id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
